import { TOKEN_RECOVERY_EPISODE_TIMEOUT_MS } from '../tokenRecovery';
import { createTokenRequestDiagnosticSummary } from '../tokenResponseDiagnostics';
import {
  applyTokenRecoveryOwner,
  createTokenRecoveryDiagnostic,
  sanitizeTokenRecoveryDiagnostic,
  type TokenRecoveryDiagnostic
} from '../tokenRecoveryDiagnostics';
import { getCanonicalAuthUserIdFromToken } from '../userIdentification';
import {
  createDestroyedRefreshResult,
  createDifferentAuthUserRefreshResult
} from '../refreshResult';
import { createCoordinationFailure } from './CoordinationRecoveryReporter';
import type {
  TokenData,
  TokenRefreshSuccess,
  TokenRefreshFailure,
  TokenRefreshAttemptResult,
  TokenUpdateCallback
} from './types';

export interface PendingRefreshRequest {
  readonly requestedByUserId: string;
  readonly requestStartedAt: number;
  readonly recovery?: TokenRecoveryDiagnostic;
}

interface TrackedRefreshRequest extends PendingRefreshRequest {
  resolve: (result: TokenRefreshAttemptResult) => void;
  timeoutId: number;
}

interface RefreshRequestContext {
  tabId: string;
  getLeaderId: () => string | null;
  canApplyToken: (
    requestedUserId: string | null | undefined,
    responseUserId: string | null
  ) => boolean;
}

export const getTokenResponseUserId = (data: TokenData): string | null =>
  data.userId || getCanonicalAuthUserIdFromToken(data.accessToken);

export const getTokenRefreshRequestStartedAt = (
  requestId: string,
  requestStartedAt?: number
): number | undefined => {
  if (typeof requestStartedAt === 'number' && Number.isFinite(requestStartedAt)) {
    return requestStartedAt;
  }

  const legacyTimestamp = requestId.match(/-(\d+)-[^-]+$/)?.[1];
  const parsedTimestamp = legacyTimestamp === undefined ? Number.NaN : Number(legacyTimestamp);

  return Number.isFinite(parsedTimestamp) ? parsedTimestamp : undefined;
};

const createUnusableSharedTokenRefreshResult = (): TokenRefreshAttemptResult => ({
  success: false,
  error: 'Coordinated token refresh result was not usable in this tab',
  terminalFailure: false,
  refreshLocally: true
});

export class RefreshRequests {
  private readonly pending = new Map<string, TrackedRefreshRequest>();

  private onTokenUpdate: TokenUpdateCallback | null = null;

  private readonly context: RefreshRequestContext;

  private isDestroyed = false;

  constructor(context: RefreshRequestContext) {
    this.context = context;
  }

  public setTokenUpdateCallback(callback: TokenUpdateCallback): void {
    this.onTokenUpdate = callback;
  }

  public get(requestId: string): PendingRefreshRequest | undefined {
    return this.pending.get(requestId);
  }

  public isPending(requestId: string, request: PendingRefreshRequest): boolean {
    return this.pending.get(requestId) === request;
  }

  public create(
    requestedByUserId: string,
    requestStartedAt: number,
    recovery?: TokenRecoveryDiagnostic
  ): { requestId: string; response: Promise<TokenRefreshAttemptResult> } {
    const requestId = `${this.context.tabId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    if (this.isDestroyed) {
      return { requestId, response: Promise.resolve(createDestroyedRefreshResult()) };
    }

    const response = new Promise<TokenRefreshAttemptResult>(resolve => {
      const request: TrackedRefreshRequest = {
        resolve,
        requestedByUserId,
        requestStartedAt,
        recovery,
        timeoutId: window.setTimeout(() => {
          this.complete(
            requestId,
            request,
            createCoordinationFailure(
              {
                success: false,
                error: 'Timed out waiting for coordinated token refresh',
                terminalFailure: false
              },
              recovery,
              'leader_timeout'
            )
          );
        }, TOKEN_RECOVERY_EPISODE_TIMEOUT_MS)
      };
      this.pending.set(requestId, request);
    });

    return { requestId, response };
  }

  public complete(
    requestId: string,
    request: PendingRefreshRequest,
    result: TokenRefreshAttemptResult
  ): void {
    const pendingRequest = this.pending.get(requestId);
    if (!pendingRequest || pendingRequest !== request) {
      return;
    }

    clearTimeout(pendingRequest.timeoutId);
    this.pending.delete(requestId);
    pendingRequest.resolve(result);
  }

  public destroy(): void {
    this.isDestroyed = true;
    this.pending.forEach((request, requestId) => {
      this.complete(requestId, request, createDestroyedRefreshResult());
    });
  }

  public handleStarted(data: unknown): void {
    if (
      !data ||
      typeof data !== 'object' ||
      !('requestId' in data) ||
      typeof data.requestId !== 'string' ||
      !('recovery' in data)
    ) {
      return;
    }

    const pendingRequest = this.pending.get(data.requestId);
    const ownerRecovery = sanitizeTokenRecoveryDiagnostic(data.recovery);

    if (!pendingRequest?.recovery || !ownerRecovery) {
      return;
    }

    applyTokenRecoveryOwner(pendingRequest.recovery, ownerRecovery);
  }

  public handleSuccess(data: TokenRefreshSuccess, authUserId: string | null): void {
    const responseUserId = getTokenResponseUserId(data);

    if (data.requestId) {
      const pendingRequest = this.pending.get(data.requestId);
      if (pendingRequest) {
        this.complete(
          data.requestId,
          pendingRequest,
          this.applySharedToken(data, pendingRequest.requestedByUserId, pendingRequest.recovery)
        );
      }
      return;
    }

    const result = this.applySharedToken(data, authUserId);

    this.pending.forEach((pendingRequest, requestId) => {
      const { requestedByUserId, recovery } = pendingRequest;

      if (requestedByUserId !== responseUserId) {
        return;
      }

      const { diagnosticSummary } = result;
      let requestResult = result;

      if (!result.success && diagnosticSummary) {
        const requestRecovery = { ...diagnosticSummary.recovery! };

        if (recovery) {
          requestRecovery.trigger = recovery.trigger;
          requestRecovery.role = recovery.role;
        }

        requestResult = {
          ...result,
          diagnosticSummary: {
            ...diagnosticSummary,
            recovery: requestRecovery
          }
        };
      }

      this.complete(requestId, pendingRequest, requestResult);
    });
  }

  public handleFailure(data: TokenRefreshFailure): void {
    const pendingRequest = this.pending.get(data.requestId);
    if (pendingRequest) {
      const refreshResult: TokenRefreshAttemptResult = {
        success: false,
        error: data.error,
        status: data.status,
        terminalFailure: Boolean(data.terminalFailure)
      };

      const diagnosticSummary = createTokenRequestDiagnosticSummary(data.diagnosticSummary);
      if (diagnosticSummary) {
        refreshResult.diagnosticSummary = diagnosticSummary;
      } else if (pendingRequest.recovery) {
        refreshResult.diagnosticSummary = createCoordinationFailure(
          refreshResult,
          {
            ...pendingRequest.recovery,
            ownerResponded: true
          },
          'owner_failure_unknown'
        ).diagnosticSummary;
      }

      if (data.refreshLocally) {
        refreshResult.refreshLocally = true;
      }

      this.complete(data.requestId, pendingRequest, refreshResult);
    }
  }

  private applySharedToken(
    data: TokenRefreshSuccess,
    requestedByUserId: string | null | undefined,
    requestRecovery = createTokenRecoveryDiagnostic(
      'token_demand',
      this.context.tabId,
      this.context.getLeaderId(),
      'follower'
    )
  ): TokenRefreshAttemptResult {
    const ownerRecovery = sanitizeTokenRecoveryDiagnostic(data.recovery);
    const recovery = {
      ...requestRecovery,
      ownerResponded: true
    };

    if (ownerRecovery) {
      applyTokenRecoveryOwner(recovery, ownerRecovery);
    }

    if (!this.context.canApplyToken(requestedByUserId, getTokenResponseUserId(data))) {
      return createCoordinationFailure(
        createDifferentAuthUserRefreshResult(),
        recovery,
        'user_mismatch'
      );
    }

    const application = this.onTokenUpdate?.({
      accessToken: data.accessToken,
      expiresAt: data.expiresAt,
      lifetimeSnapshot: data.lifetimeSnapshot,
      userId: getTokenResponseUserId(data)
    });

    if (application === true) {
      return { success: true };
    }

    if (!application) {
      return createCoordinationFailure(
        createUnusableSharedTokenRefreshResult(),
        recovery,
        'shared_token_unusable'
      );
    }

    if (application.applied) {
      return { success: true };
    }

    return createCoordinationFailure(
      createUnusableSharedTokenRefreshResult(),
      recovery,
      application.reason
    );
  }
}
